import { type FC } from 'react'

import { Magic } from '@syconium/little-miss-figgy'
import { TypeStyle } from '@syconium/little-miss-figgy'

import { type SummaryTopic } from '../../../__generated__/graphql/catalog/graphql'
import { useFeatureFlag } from '../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'

import {
  GreenCheck,
  ReviewSummaryContainer,
  ReviewSummaryHeader,
  ReviewSummaryHeaderTitle,
  ReviewSummaryList,
  ReviewSummaryTag,
} from './styles'

const ReviewSummaryTopic: FC<SummaryTopic> = ({ tag, generatedSummary }) => {
  return (
    <div>
      <ReviewSummaryTag>
        <GreenCheck height={16} width={16} />
        <TypeStyle.HeadlineExtraExtraSmall>{tag}</TypeStyle.HeadlineExtraExtraSmall>
      </ReviewSummaryTag>
      <TypeStyle.BodySmall fontWeight={400} asTag='p'>
        {generatedSummary}
      </TypeStyle.BodySmall>
    </div>
  )
}

type Props = {
  reviewSummary: Array<SummaryTopic>
  totalReviewCount: number
}

export const ReviewSummary: FC<Props> = ({ reviewSummary, totalReviewCount }) => {
  const { languageGroup } = useLocalization()
  const isEnglish = languageGroup === 'en'
  const {
    detailsSection: {
      reviewSummary: { title, description },
    },
  } = useTranslation()

  const flag = useFeatureFlag<'true' | 'false'>({
    key: 'ai-review-summary',
    controlVariant: 'false',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
  })
  const isReviewSummaryEnabled = flag === 'true'

  const formatted = new Intl.NumberFormat('en').format(totalReviewCount)
  const descriptionCopy = description.replace('{{totalReviewCount}}', formatted)

  if (!isEnglish || !isReviewSummaryEnabled) return null

  return (
    <ReviewSummaryContainer>
      <ReviewSummaryHeader>
        <Magic />
        <ReviewSummaryHeaderTitle>{title}</ReviewSummaryHeaderTitle>
      </ReviewSummaryHeader>

      <ReviewSummaryList>
        <TypeStyle.BodySmall fontWeight={400} asTag='p'>
          {descriptionCopy}
        </TypeStyle.BodySmall>
        {reviewSummary.map(summary => (
          <ReviewSummaryTopic
            key={summary.tag}
            tag={summary.tag}
            generatedSummary={summary.generatedSummary}
          />
        ))}
      </ReviewSummaryList>
    </ReviewSummaryContainer>
  )
}
